import React, { useCallback, useEffect, useMemo, useRef } from "react";
import {
  FlatList,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  ViewStyle,
} from "react-native";
import Icon from "../components/Icon";
import variables from "../styles/variables";

type ScrollDirection = { type: "left" | "right" };

type ScrollControlProps = {
  scrollRef: React.RefObject<ScrollView> | React.RefObject<FlatList>;
  elementDistance: number;
  childrenWidth?: number;
  parentWidth?: number;
};

const sidePadding = variables.spacing.xxsmall;

const setHidden = (nodeRef: React.RefObject<any>, hidden: boolean) => {
  const node = nodeRef.current;
  if (!node) return;

  // Web: DOM node styles
  if (Platform.OS === "web") {
    // RNW typically gives a DOM element here
    try {
      node.style.opacity = hidden ? 0 : 0.2;
      node.style.pointerEvents = hidden ? "none" : "auto";
      node.style.display = hidden ? "none" : "flex";
    } catch {
      // ignore
    }
    return;
  }

  // Native: setNativeProps
  node.setNativeProps?.({
    style: {
      opacity: hidden ? 0 : 0.2,
      // @ts-ignore RN accepts pointerEvents as a prop; setNativeProps works in practice for many components
      pointerEvents: hidden ? "none" : "auto",
    } as ViewStyle,
  });
};

export const useScrollControl = ({
  scrollRef,
  elementDistance,
  childrenWidth,
  parentWidth,
}: ScrollControlProps) => {
  const positionRef = useRef(0);

  // hide flags as refs (no state)
  const hideLeftArrowRef = useRef(true);
  const hideRightArrowRef = useRef(false);

  // arrow node refs for imperative hide/show
  const leftArrowNodeRef = useRef<any>(null);
  const rightArrowNodeRef = useRef<any>(null);

  const syncArrowVisibility = useCallback(() => {
    setHidden(leftArrowNodeRef, hideLeftArrowRef.current);
    setHidden(rightArrowNodeRef, hideRightArrowRef.current);
  }, []);

  useEffect(() => {
    if (parentWidth && childrenWidth) {
      // initial layout-based visibility
      hideRightArrowRef.current = parentWidth >= childrenWidth;
      syncArrowVisibility();
    }
  }, [childrenWidth, parentWidth, syncArrowVisibility]);

  const scrollToPosition = useCallback(
    (targetPosition: number) => {
      const node = scrollRef?.current as any;
      if (!node) return;

      if ("scrollTo" in node) {
        node.scrollTo?.({ x: targetPosition, animated: true });
      } else if ("scrollToOffset" in node) {
        node.scrollToOffset?.({ offset: targetPosition, animated: true });
      }
    },
    [scrollRef]
  );

  const handleHideArrow = useCallback(
    ({
      layoutMeasurement,
      contentOffset,
      contentSize,
    }: {
      layoutMeasurement: { width: number };
      contentOffset: { x: number };
      contentSize: { width: number };
    }) => {
      hideLeftArrowRef.current = contentOffset.x - sidePadding < 0;
      hideRightArrowRef.current =
        layoutMeasurement.width + contentOffset.x >=
        contentSize.width - sidePadding;

      syncArrowVisibility();
    },
    [syncArrowVisibility]
  );

  const handleLayout = useCallback(
    (parentW: number, childrenW: number) => {
      hideRightArrowRef.current = parentW >= childrenW;
      syncArrowVisibility();
    },
    [syncArrowVisibility]
  );

  const ScrollIndicator = useMemo(() => {
    return function ScrollIndicatorInner({ type }: ScrollDirection) {
      const hoverRef = useRef<any>(null);
      const isLeft = type === "left";

      // point the “visibility” ref at the rendered node
      const nodeRef = isLeft ? leftArrowNodeRef : rightArrowNodeRef;

      const handleMouseEnter = () => {
        if (hoverRef.current && Platform.OS === "web") {
          try {
            hoverRef.current.children[0].style.width = 50;
            hoverRef.current.style.opacity = 0.5;
          } catch {
            // ignore
          }
        }
      };

      const handleMouseLeave = () => {
        if (hoverRef.current && Platform.OS === "web") {
          try {
            hoverRef.current.children[0].style.width = 20;
            hoverRef.current.style.opacity = 0.2;
          } catch {
            // ignore
          }
        }
      };

      const style = isLeft ? styles.leftArrow : styles.rightArrow;

      return (
        <Pressable
          // keep hover behavior ref
          ref={(el) => {
            hoverRef.current = el;
            nodeRef.current = el;
            // apply current hidden state immediately on mount
            if (isLeft) setHidden(leftArrowNodeRef, hideLeftArrowRef.current);
            else setHidden(rightArrowNodeRef, hideRightArrowRef.current);
          }}
          // @ts-ignore
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          style={[styles.background, style]}
          onPress={() => {
            const targetPosition = isLeft
              ? positionRef.current - elementDistance
              : positionRef.current + elementDistance;

            scrollToPosition(targetPosition);
          }}
        >
          <Icon
            name="chevronLeft"
            size={"small"}
            fill="palette.rtv.white"
            rotate={isLeft ? 0 : 180}
          />
        </Pressable>
      );
    };
  }, [elementDistance, scrollToPosition, syncArrowVisibility]);

  return {
    // still expose position via getter-style access
    positionRef,
    setPosition: (pos: number) => {
      positionRef.current = pos;
    },

    // expose hide refs if you need them (but note: changing them won't re-render)
    hideLeftArrowRef,
    hideRightArrowRef,

    handleHideArrow,
    handleLayout,
    ScrollIndicator,

    // optional: let callers force a resync if they mutate refs externally
    syncArrowVisibility,
  };
};

const styles = StyleSheet.create({
  background: {
    backgroundColor: variables.colors.palette.rtv.black,
    opacity: 0.2,
    alignItems: "center",
    justifyContent: "center",
    position: "absolute",
    height: "100%",
  },
  leftArrow: { left: 0, zIndex: 1 },
  rightArrow: { right: 0, zIndex: 1 },
});
